import React, { useContext, useRef, useEffect, useState } from 'react';
import { Card, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import PageTitle from '../layouts/PageTitle';
import { CommonCollections } from '../../App';
import { strapiUrl } from '../../utils/constants';
import SectionalCommittees from './SectionalCommittees';
import DownloadFile from './Forms/Elements/DownloadFile';
import { useStaticCollections } from '../../context/StaticCollections';
import { useAdminCollections } from '../../context/AdminCollections';
import { useLoggedInUserContextProvider } from '../../context/LoggedInUserContext';
import { getReportRoundYear } from '../../utils/getReportRoundYear';
import { calculateColumnWidths } from '../../utils/calculateColumnWidths';
import { calculateDynamicStyle } from '../../utils/calculateDynamicStyle';
import { useAdminActionCollections } from '../../context/AdminActionCollections';
import ModalConfirmation from './ModalConfirmation';
import { axiosPost } from '../../utils/axiosPost';

const TableIndependentAssessor = ({ nominationsInSectionalCommittee }) => {
  const {
    activeFellows,
    declineReasons,
    refereeStatuses,
    sectionalCommittees,
  } = useStaticCollections();
  const { candidates } = useAdminCollections();
  const {
    refereeReports,
    setNominationId,
    nonAasReferees,
    setRefereeReportId,
    refreshRefereeReports,
    refreshNonAasReferees,
    setRefreshRefereeReports,
    setRefreshNonAasReferees,
    refreshDynamicCollections,
    setRefreshDynamicCollections,
  } = useContext(CommonCollections);
  const { userDetails } = useLoggedInUserContextProvider();

  const cellRefs = useRef([]);
  const [columnWidths, setColumnWidths] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [modalBodyText, setModalBodyText] = useState('');
  const [deleteAssessor, setDeleteAssessor] = useState(false);
  const [refereeData, setRefereeData] = useState({});
  const { disableCommitteeAccess } = useAdminActionCollections();
  const isAdmin =
    userDetails?.roles?.includes('super-admin') ||
    userDetails?.roles?.includes('admin');

  const handleSelectClick = (referee, isRemove) => {
    setShowModal(true);
    setRefereeData(referee);
    setDeleteAssessor(isRemove);
    if (isRemove) {
      setModalBodyText(
        `Are you sure you want to remove **${referee.refereeName}** as an Independent Assessor?`,
      );
    } else {
      setModalBodyText(
        `Are you sure you want to reuse **${referee.refereeName}** as an Independent Assessor?`,
      );
    }
  };

  const handleCancelSelect = () => {
    setShowModal(false);
    setRefereeData({});
  };

  const handleConfirmSelect = async () => {
    if (deleteAssessor) {
      try {
        const response = await axiosPost('independent-assessor-remove', {
          nominationId: refereeData.attributes.nominationId,
          refereeReportId: refereeData.id,
        });
        if (response.status === 200) {
          setShowModal(false);
          setRefereeData({});
          setRefreshRefereeReports(!refreshRefereeReports);
          setRefreshDynamicCollections(!refreshDynamicCollections);
          setRefreshNonAasReferees(!refreshNonAasReferees);
        }
        // Delete independent assessor
      } catch (error) {
        console.error('Error submitting independent assessor form:', error);
      }
    } else {
      try {
        const response = await axiosPost('independent-assessor-reuse', {
          data: refereeData,
        });
        if (response.status === 200) {
          setShowModal(false);
          setRefereeData({});
          setRefreshRefereeReports(!refreshRefereeReports);
          setRefreshDynamicCollections(!refreshDynamicCollections);
          setRefreshNonAasReferees(!refreshNonAasReferees);
        }
      } catch (error) {
        console.error('Error submitting independent assessor form:', error);
      }
    }
  };
  useEffect(() => {
    setColumnWidths(calculateColumnWidths(cellRefs.current, 150, 250));
  }, [nominationsInSectionalCommittee.length]);
  return (
    <>
      {nominationsInSectionalCommittee.map((nomination) => {
        const filteredReports = refereeReports.filter(
          (report) =>
            report.attributes.nominationId === nomination.id &&
            !report.attributes.refereeArchived,
        );
        const linkAssessorText = 'Add assessors';
        const linkAssessorIcon = 'plus';

        return (
          <Card
            key={nomination.id}
            className="card-two mb-xl-5 table-responsive"
          >
            <Card.Body>
              <Card.Title className="mb-xl-4">
                <div className="row">
                  <div className="col d-flex justify-content-between align-items-center">
                    <div>
                      {candidates[nomination.attributes.candidateId]}
                      <Link
                        to="/form-view"
                        onClick={() => {
                          setNominationId(nomination.id);
                        }}
                      >
                        <span title="View">
                          <i className="bi bi-eye view-button" />
                        </span>
                      </Link>
                    </div>
                    {((nomination.attributes.electionTypeId === 2 &&
                      filteredReports.length < 9) ||
                      (nomination.attributes.electionTypeId === 1 &&
                        filteredReports.length < 7) ||
                      (nomination.attributes.electionTypeId === 3 &&
                        filteredReports.length < 7)) &&
                      (userDetails && disableCommitteeAccess && !isAdmin
                        ? null
                        : ['committee-chair', 'admin', 'super-admin'].some(
                            (role) => userDetails.roles?.includes(role),
                          ) && (
                            <Link
                              to="/independent-assessor"
                              onClick={() => {
                                setNominationId(nomination.id);
                                setRefereeReportId(null);
                              }}
                              onKeyPress={(event) => {
                                if (event.key === 'Enter') {
                                  setNominationId(nomination.id);
                                  setRefereeReportId(null);
                                }
                              }}
                            >
                              <button type="button" className="btn btn-success">
                                <span className="btn-icon-start text-info">
                                  <i
                                    className={`fa fa-${linkAssessorIcon} color-info`}
                                  />
                                </span>
                                {linkAssessorText}
                              </button>
                            </Link>
                          ))}
                  </div>
                </div>
              </Card.Title>
              <Table bordered hover striped className="dataTable">
                <thead className="thead-dark">
                  <tr>
                    <th>
                      <b>Name</b>
                    </th>
                    <th>
                      <b>Status</b>
                    </th>
                    <th>
                      <b>Type</b>
                    </th>
                    <th>
                      <b>Round</b>
                    </th>
                    <th aria-label="Actions">
                      <b />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReports.map((referee, index) => {
                    const reportYear = getReportRoundYear(referee.attributes);
                    const candidateName =
                      candidates[nomination.attributes.candidateId];
                    const candidateWithoutSpaces = candidateName
                      ? candidateName.replace(/\s/g, '')
                      : '';
                    referee.refereeName = referee.attributes.refereeFellowId
                      ? activeFellows[referee.attributes.refereeFellowId]
                      : nonAasReferees[referee.attributes.nonAasRefereeId];

                    referee.candidate =
                      candidates[nomination.attributes.candidateId];
                    referee.electionTypeId =
                      nomination.attributes.electionTypeId;
                    referee.sectionalCommittee =
                      sectionalCommittees[
                        nomination.attributes.sectionalCommitteeId
                      ];
                    referee.testEmail = userDetails.email;
                    let reason = '';
                    const {
                      refereeStatusId,
                      declineReasonId,
                      declineReasonOther,
                      extensionReason,
                      extensionReasonApprovalNote,
                    } = referee.attributes;

                    if (refereeStatusId === 3) {
                      reason =
                        declineReasonId === 5
                          ? declineReasonOther
                          : declineReasons[declineReasonId];
                    } else if (refereeStatusId === 5) {
                      reason = extensionReason;
                    } else if (refereeStatusId === 6) {
                      reason = extensionReasonApprovalNote;
                    }
                    return (
                      <tr key={index}>
                        <td
                          ref={(el) => (cellRefs.current[index] = el)}
                          style={calculateDynamicStyle(columnWidths[0])}
                        >
                          {referee.refereeName}
                        </td>
                        <td
                          ref={(el) =>
                            (cellRefs.current[index + filteredReports.length] =
                              el)
                          }
                          style={calculateDynamicStyle(columnWidths[1])}
                        >
                          {reason ? (
                            <>
                              <span>
                                <b>
                                  {
                                    refereeStatuses[
                                      referee.attributes.refereeStatusId
                                    ]
                                  }
                                  {' : '}
                                </b>
                              </span>
                              {refereeStatusId === 6 ? (
                                <>
                                  <br />
                                  <span>Reason: {extensionReason}</span>
                                  <br />
                                  <span>
                                    Approval Note: {extensionReasonApprovalNote}
                                  </span>
                                </>
                              ) : (
                                <span>{reason}</span>
                              )}
                            </>
                          ) : (
                            <span>
                              {
                                refereeStatuses[
                                  referee.attributes.refereeStatusId
                                ]
                              }
                            </span>
                          )}
                        </td>
                        <td
                          ref={(el) =>
                            (cellRefs.current[
                              index + filteredReports.length * 2
                            ] = el)
                          }
                          style={calculateDynamicStyle(columnWidths[2])}
                        >
                          {referee.attributes.independentAssessor === true
                            ? 'Independent Assessor'
                            : 'Referee'}
                        </td>
                        <td
                          ref={(el) =>
                            (cellRefs.current[
                              index + filteredReports.length * 3
                            ] = el)
                          }
                          style={calculateDynamicStyle(columnWidths[3])}
                        >
                          {reportYear}
                        </td>
                        <td
                          ref={(el) =>
                            (cellRefs.current[
                              index + filteredReports.length * 4
                            ] = el)
                          }
                          style={calculateDynamicStyle(columnWidths[4])}
                          className="text-center"
                        >
                          {referee.attributes.report.data !== null && (
                            <DownloadFile
                              text={`Report Uploaded Year : ${reportYear}`}
                              url={`${strapiUrl}${referee.attributes.report.data.attributes.url}`}
                              name={`${candidateWithoutSpaces}${referee.refereeName}`}
                              buttonClassName="action-button-two"
                              iconClassName="download-button"
                              showFileName={false}
                              showText={false}
                            />
                          )}

                          {referee.attributes.independentAssessor === true && (
                            <>
                              {referee.attributes.requestEmailDate && (
                                <Link
                                  to="/independent-assessor"
                                  onClick={() => {
                                    setNominationId(
                                      referee.attributes.nominationId,
                                    );
                                    setRefereeReportId(referee.id); // Set the refereeId here
                                  }}
                                  onKeyPress={(event) => {
                                    if (event.key === 'Enter') {
                                      setNominationId(
                                        referee.attributes.nominationId,
                                      );
                                      setRefereeReportId(referee.id); // Set the refereeId here
                                    }
                                  }}
                                >
                                  <i className="bi bi-pen action-button" />
                                </Link>
                              )}
                              {!referee.attributes.requestEmailDate && (
                                <Link
                                  onClick={() =>
                                    handleSelectClick(referee, false)
                                  }
                                >
                                  <span title="Reuse Independent Assessor">
                                    <i className="bi bi-arrow-repeat view-button" />
                                  </span>
                                </Link>
                              )}
                              <Link
                                onClick={() => handleSelectClick(referee, true)}
                              >
                                <span title="Remove Independent Assessor">
                                  <i className="bi bi-trash action-button bg-danger" />
                                </span>
                              </Link>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        );
      })}
      <ModalConfirmation
        showModal={showModal}
        handleCancelSelect={handleCancelSelect}
        bodyText={modalBodyText}
        handleConfirmSelect={handleConfirmSelect}
      />
    </>
  );
};

const IndependentAssessor = () => {
  const { disableCommitteeAccess } = useAdminActionCollections();

  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationTitle, setNotificationTitle] = useState('');

  useEffect(() => {
    if (disableCommitteeAccess) {
      setNotificationMessage(
        ' Independent Assessor submissions are now closed, if you are required to submit a report for any candidate, please contact fellowship@science.org.au.',
      );
      setNotificationTitle('Independent Assessor submissions are now closed');
    }
  }, [disableCommitteeAccess]);

  return (
    <>
      <PageTitle
        motherMenu="Nominations"
        activeMenu="Independent Assessors"
        notificationMessage={notificationMessage}
        notificationTitle={notificationTitle}
      />

      <SectionalCommittees
        TableComponent={TableIndependentAssessor}
        statusTypes={[3]}
      />
    </>
  );
};

TableIndependentAssessor.propTypes = {
  nominationsInSectionalCommittee: PropTypes.array,
};

export default IndependentAssessor;
